import { Box, Checkbox } from '@chakra-ui/react'
import React, { LegacyRef } from 'react'
import { usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { maxPageCount } from '.'

const CHECKBOX_HIT_PADDING_X = '15px'
const CHECKBOX_HIT_PADDING_Y = '20px'
export const CHECKBOX_COLUMN_ID = 'selection'

const ALL_ROW_ACTION_TYPES = [
  'toggleRowSelected',
  'toggleAllRowsSelected',
  'toggleAllPageRowsSelected',
]

const IndeterminateCheckbox = React.forwardRef<
  HTMLInputElement,
  { indeterminate?: boolean }
>(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef()
  const resolvedRef = ref || defaultRef

  React.useEffect(() => {
    // TypeScript doesn't recognize indeterminate property on HTMLInputElement
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolvedRef.current.indeterminate = indeterminate
  }, [resolvedRef, indeterminate])

  return (
    <Box position="relative">
      <Checkbox
        colorScheme="doppel"
        isChecked={(rest as HTMLInputElement).checked}
        isIndeterminate={indeterminate}
        key="checkbox"
        ref={resolvedRef as LegacyRef<HTMLInputElement>}
        size="md"
        {...rest}
      >
        {/* This box is a click area for the checkbox */}
        <Box
          display="block"
          h={`calc(100% + 2 * ${CHECKBOX_HIT_PADDING_Y})`}
          left={`-${CHECKBOX_HIT_PADDING_X}`}
          onClickCapture={(e) => e.stopPropagation()}
          position="absolute"
          top={`-${CHECKBOX_HIT_PADDING_Y}`}
          w={`calc(100% + 2 * ${CHECKBOX_HIT_PADDING_X})`}
        />
      </Checkbox>
    </Box>
  )
})

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox'

export const calculateTotalPageCount = (data, selectedPageSize) => {
  if (!data) return 1

  const numResults = data?.aggregate?.count || 0

  return maxPageCount(numResults, selectedPageSize)
}

export function useCreateTable(
  columns,
  data,
  selectedPage,
  selectedPageSize,
  totalPageCount,
  getRowId = undefined,
  enableBulkActions = true,
  rowSelectionState = undefined,
  setRowSelectionState = undefined,
) {
  const tableColumns = React.useMemo(() => {
    const cols = [...columns]

    // Conditionally add the selection column only when bulk actions are enabled
    if (enableBulkActions) {
      cols.unshift({
        id: CHECKBOX_COLUMN_ID,
        Header: ({ getToggleAllPageRowsSelectedProps }) => (
          <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
        ),
        Cell: ({ row }) => (
          <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
        ),
      })
    }

    return cols
  }, [columns, enableBulkActions])

  return useTable(
    {
      columns: tableColumns,
      data,
      getRowId,
      autoResetSelectedRows: false,
      initialState: {
        pageIndex: selectedPage,
        pageSize: selectedPageSize,
        hiddenColumns: tableColumns
          .filter((col: any) => col.show === false)
          .map((col) => {
            if (!col.id) {
              throw new Error(`Column ${col.Header} must have an id to be hidden`)
            }
            return col.id
          }) as any,
        selectedRowIds: rowSelectionState || {},
      },
      stateReducer: (newState, action, oldState) => {
        const returnedState = { ...newState }
        if (action.type === 'toggleAllRowsSelected' && !action.value) {
          // make this clear out all rows, as is intended
          returnedState.selectedRowIds = {}
        }
        if (ALL_ROW_ACTION_TYPES.includes(action.type)) {
          // copy state into jotai
          setRowSelectionState?.(returnedState.selectedRowIds)
        }
        return returnedState
      },
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            pageIndex: selectedPage,
            pageSize: selectedPageSize,
          }),
          [state, selectedPage, selectedPageSize],
        )
      },
      manualPagination: true,
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: totalPageCount,
      autoResetSortBy: false,
    },
    useSortBy,
    usePagination,
    useRowSelect,
  )
}

// For backward compatibility with existing code
// eslint-disable-next-line react-hooks/rules-of-hooks
export const createTable = useCreateTable

export const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }
}
